'use client';

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { format } from 'date-fns';
import {
  AlertTriangle,
  Calendar,
  Camera,
  CheckCircle,
  FileText,
  Shield,
  Upload,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { useUser } from '@kit/supabase/hooks/use-user';
import { Alert, AlertDescription } from '@kit/ui/alert';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@kit/ui/card';
import { DateField } from '@kit/ui/date-field';
import { FileUploader } from '@kit/ui/file-uploader';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

// Import types and services
import type { IdentityVerification } from '../../schemas/dancer-form.schema';
import IdentityDocumentConfigService, {
  type DocumentType,
} from '../../services/identity-document-config.service';
import { CountrySelector } from '../country/country-selector';
import { identityCountryChange } from './identity-country-change';
import { StorageImage } from './storage-image';

interface IdentityVerificationFieldError {
  countryCode?: string;
  documentType?: string;
  documentNumber?: string;
  frontImageUrl?: string;
  backImageUrl?: string;
  selfieImageUrl?: string;
  expiryDate?: string;
  issueDate?: string;
  notes?: string;
}

interface IdentityVerificationInputProps {
  value?: IdentityVerification;
  onChange: (identity: IdentityVerification) => void;
  onBlur?: () => void;
  errors?: IdentityVerificationFieldError;
  required?: boolean;
  className?: string;
  label?: string;
  hint?: string;
  documentOnly?: boolean; // New prop to disable file uploads
}

// Default identity verification object
// NOTE: verification_status NOT included - users cannot set their own verification status
// The database handles setting the default 'pending' status automatically
const DEFAULT_IDENTITY: IdentityVerification = {
  countryCode: 'CH',
  documentType: undefined,
  documentNumber: '',
  frontImageUrl: '',
  backImageUrl: '',
  selfieImageUrl: '',
  expiryDate: undefined,
  issueDate: undefined,
  notes: '',
};

export function IdentityVerificationInput({
  value,
  onChange,
  onBlur,
  errors,
  required = false,
  className,
  label,
  hint,
  documentOnly = false,
}: IdentityVerificationInputProps) {
  const supabase = useSupabase();
  const { data: user } = useUser();
  const { t } = useTranslation();
  const fieldId = useId();
  const resolvedLabel = label ?? t('dancer:identityVerification.label');

  const currentValue = value || DEFAULT_IDENTITY;

  // Use a ref to always have access to the latest currentValue
  // This prevents stale closure issues when async operations (like file uploads) complete
  const currentValueRef = useRef(currentValue);
  useEffect(() => {
    currentValueRef.current = currentValue;
  }, [currentValue]);

  // Local state for document number to avoid lag (defined before updateField to avoid hoisting issues)
  const [localDocumentNumber, setLocalDocumentNumber] = useState(
    currentValue.documentNumber || '',
  );

  // Min selectable expiry date = tomorrow. Computed once via a lazy useState
  // initializer (runs at mount, not during render) so the impure `Date.now()`
  // call doesn't violate the React purity rule and isn't recomputed per render.
  const [minVerificationExpiryDate] = useState(() =>
    format(new Date(Date.now() + 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
  );
  // An issue date is in the past; DateField no longer caps unbounded fields
  // at today, so the cap is explicit (2026-10-07).
  const [todayIsoDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  // Update individual fields - use ref to avoid stale closure issues
  // This is critical for async operations like file uploads that may complete
  // after the parent state has changed
  const updateField = useCallback(
    (field: keyof IdentityVerification, newValue: unknown) => {
      onChange({ ...currentValueRef.current, [field]: newValue });
    },
    [onChange],
  );

  // Sync local state when external value changes (e.g., from database load)
  // Only sync if external value changed and local state is empty (initial load)
  // or if external value is being set from outside (not from our debounced update)
  const [lastSyncedValue, setLastSyncedValue] = useState(
    currentValue.documentNumber || '',
  );

  useEffect(() => {
    // Only sync if the external value changed from something other than our last update
    if (
      currentValue.documentNumber !== lastSyncedValue &&
      currentValue.documentNumber !== localDocumentNumber
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional controlled<->local sync: mirrors the external `currentValue.documentNumber` prop into local input state only when it actually diverges (guarded above), to avoid input lag.
      setLocalDocumentNumber(currentValue.documentNumber || '');
      setLastSyncedValue(currentValue.documentNumber || '');
    }
  }, [currentValue.documentNumber, lastSyncedValue, localDocumentNumber]);

  // Debounced update to parent - only update after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localDocumentNumber !== currentValue.documentNumber) {
        updateField('documentNumber', localDocumentNumber);
        setLastSyncedValue(localDocumentNumber);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [localDocumentNumber, currentValue.documentNumber, updateField]);

  // Initialize document type with preferred document for the country if not already set
  // This ensures validation can trigger on form mount with default country
  // Must run whenever countryCode changes or documentType is cleared
  useEffect(() => {
    if (currentValue.countryCode && !currentValue.documentType) {
      const config = IdentityDocumentConfigService.getCountryConfig(
        currentValue.countryCode,
      );
      if (config?.preferredDocument) {
        updateField('documentType', config.preferredDocument);
      }
    }
  }, [currentValue.countryCode, currentValue.documentType, updateField]);

  // Get country configuration
  // IMPORTANT: All hooks must be called before any early returns to avoid
  // "Rendered more hooks than during the previous render" errors
  const countryConfig = useMemo(() => {
    if (!currentValue.countryCode) return null;
    return IdentityDocumentConfigService.getCountryConfig(
      currentValue.countryCode,
    );
  }, [currentValue.countryCode]);

  // Get document requirement
  const documentRequirement = useMemo(() => {
    if (!currentValue.countryCode || !currentValue.documentType) return null;
    return IdentityDocumentConfigService.getDocumentRequirement(
      currentValue.countryCode,
      currentValue.documentType,
    );
  }, [currentValue.countryCode, currentValue.documentType]);

  // Handle country change
  // The country the entered document number and photos belong to; survives a
  // clear so re-picking it does not wipe them (see identityCountryChange).
  const docsCountryRef = useRef(value?.countryCode ?? '');

  const handleCountryChange = useCallback(
    (countryCode: string) => {
      if (countryCode === currentValue.countryCode) return;
      if (currentValue.countryCode)
        docsCountryRef.current = currentValue.countryCode;

      const { value: updatedValue, docsCountry } = identityCountryChange(
        currentValue,
        countryCode,
        docsCountryRef.current,
        documentOnly,
      );
      if (docsCountry !== docsCountryRef.current) {
        // Moved to another country: the old document number is void.
        setLocalDocumentNumber('');
      }
      docsCountryRef.current = docsCountry;

      onChange(updatedValue);
    },
    [currentValue, onChange, documentOnly],
  );

  // Handle document type change
  const handleDocumentTypeChange = useCallback(
    (documentType: DocumentType) => {
      if (documentType === currentValue.documentType) return;

      const updatedValue = {
        ...currentValue,
        documentType,
        documentNumber: '',
      };

      // Only clear image URLs if not in document-only mode
      if (!documentOnly) {
        updatedValue.frontImageUrl = '';
        updatedValue.backImageUrl = '';
        updatedValue.selfieImageUrl = '';
      }

      // Clear local document number state
      setLocalDocumentNumber('');

      onChange(updatedValue);
    },
    [currentValue, onChange, documentOnly],
  );

  // Handle file upload success (only needed when not in document-only mode)
  const handleFileUploadSuccess = useCallback(
    (field: string) => (files: string[]) => {
      if (files.length > 0) {
        updateField(field as keyof IdentityVerification, files[0]);
      }
    },
    [updateField],
  );

  // Validate document number - only when fully typed (debounced via localDocumentNumber)
  const documentNumberValidation = useMemo(() => {
    // Only validate when we have all required fields and user has stopped typing
    if (
      !currentValue.countryCode ||
      !currentValue.documentType ||
      !currentValue.documentNumber
    ) {
      return { isValid: true };
    }
    // Skip validation if user is still typing (local differs from parent)
    if (localDocumentNumber !== currentValue.documentNumber) {
      return { isValid: true };
    }
    return IdentityDocumentConfigService.validateDocumentNumber(
      currentValue.countryCode,
      currentValue.documentType,
      currentValue.documentNumber,
    );
  }, [
    currentValue.countryCode,
    currentValue.documentType,
    currentValue.documentNumber,
    localDocumentNumber,
  ]);

  // Don't render file uploaders until we have user data (only needed for file uploads)
  // IMPORTANT: This early return must come AFTER all hooks are called
  if (!documentOnly && !user?.id) {
    return (
      <Card className={cn('p-4', className)}>
        <CardContent className="p-0">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Shield className="text-muted-foreground mx-auto mb-2 h-8 w-8 animate-pulse" />
              <p className="text-muted-foreground text-sm">
                <Trans i18nKey="dancer:identityVerification.loading" />
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('p-4', className)}>
      <CardHeader className="p-0 pb-4">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Shield className="text-muted-foreground h-4 w-4" />
          {resolvedLabel}
          {required && <span className="text-destructive">*</span>}
        </CardTitle>
        {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
      </CardHeader>

      <CardContent className="space-y-4 p-0">
        {/* Country Selection */}
        <div className="space-y-1">
          <Label
            htmlFor={`${fieldId}-country`}
            className="flex items-center gap-1 text-xs"
          >
            <Trans i18nKey="dancer:identityVerification.issuingCountry" />{' '}
            {required && <span className="text-destructive">*</span>}
          </Label>
          <CountrySelector
            value={currentValue.countryCode ?? ''}
            id={`${fieldId}-country`}
            onValueChange={handleCountryChange}
            error={errors?.countryCode}
          />
        </div>

        {/* Document Type Selection */}
        {countryConfig && (
          <div className="space-y-1">
            <Label className="flex items-center gap-1 text-xs">
              <Trans i18nKey="dancer:identityVerification.documentType" />{' '}
              {required && <span className="text-destructive">*</span>}
            </Label>
            <Select
              value={currentValue.documentType}
              onValueChange={handleDocumentTypeChange}
            >
              <SelectTrigger
                className={cn(
                  errors?.documentType &&
                    'border-destructive focus:ring-destructive',
                )}
              >
                <SelectValue
                  placeholder={t(
                    'dancer:identityVerification.selectDocumentType',
                  )}
                >
                  {currentValue.documentType && documentRequirement && (
                    <span className="flex items-center gap-2">
                      <FileText className="h-3 w-3" />
                      <span>{documentRequirement.displayName}</span>
                    </span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {countryConfig.supportedDocuments.map(
                  (doc: (typeof countryConfig.supportedDocuments)[0]) => (
                    <SelectItem key={doc.type} value={doc.type}>
                      <div className="flex items-center gap-2">
                        <FileText className="h-3 w-3" />
                        <div>
                          <div className="font-medium">{doc.displayName}</div>
                          <div className="text-muted-foreground text-xs">
                            {doc.description}
                          </div>
                        </div>
                        {doc.type === countryConfig.preferredDocument && (
                          <Badge variant="secondary" className="text-xs">
                            <Trans i18nKey="dancer:identityVerification.recommended" />
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
            {errors?.documentType && (
              <p className="text-destructive mt-1 text-xs">
                {errors.documentType}
              </p>
            )}
            {countryConfig.notes && (
              <p className="text-muted-foreground text-xs">
                {countryConfig.notes}
              </p>
            )}
          </div>
        )}

        {/* Document Number */}
        {documentRequirement && (
          <div className="space-y-1">
            <Label className="text-xs">
              <Trans i18nKey="dancer:identityVerification.documentNumber" />{' '}
              {documentRequirement.numberPattern && (
                <span className="text-destructive">*</span>
              )}
            </Label>
            <Input
              placeholder={
                documentRequirement.numberExample ||
                t('dancer:identityVerification.enterDocumentNumber')
              }
              value={localDocumentNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLocalDocumentNumber(e.target.value)
              }
              onBlur={onBlur}
              className={cn(
                (errors?.documentNumber || !documentNumberValidation.isValid) &&
                  'border-destructive focus:ring-destructive',
              )}
            />
            {(errors?.documentNumber || documentNumberValidation.message) && (
              <p className="text-destructive mt-1 text-xs">
                {errors?.documentNumber || documentNumberValidation.message}
              </p>
            )}
            {documentRequirement.numberExample && (
              <p className="text-muted-foreground text-xs">
                <Trans
                  i18nKey="dancer:identityVerification.example"
                  values={{ example: documentRequirement.numberExample }}
                />
              </p>
            )}
          </div>
        )}

        {/* Document Images Upload */}
        {documentRequirement && !documentOnly && user?.id && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              <Label className="text-xs font-medium">
                <Trans i18nKey="dancer:identityVerification.documentImages" />
              </Label>
            </div>

            {/* Photo quality tips */}
            <div className="border-info/30 bg-info/10 rounded-md border p-3">
              <p className="text-info text-xs font-medium">
                <Trans i18nKey="dancer:identityVerification.tipsForClearPhoto" />
              </p>
              <ul className="text-info mt-1 list-inside list-disc text-xs">
                <li>
                  <Trans i18nKey="dancer:identityVerification.tipLighting" />
                </li>
                <li>
                  <Trans i18nKey="dancer:identityVerification.tipCorners" />
                </li>
                <li>
                  <Trans i18nKey="dancer:identityVerification.tipFlatSurface" />
                </li>
                <li>
                  <Trans i18nKey="dancer:identityVerification.tipReadable" />
                </li>
              </ul>
            </div>

            <div className="grid gap-4">
              {/* Front Image */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-xs">
                  <FileText className="h-3 w-3" />
                  <Trans i18nKey="dancer:identityVerification.documentFront" />{' '}
                  {required && <span className="text-destructive">*</span>}
                </Label>
                {currentValue.frontImageUrl ? (
                  <div className="relative overflow-hidden rounded-md border">
                    <StorageImage
                      storagePath={currentValue.frontImageUrl}
                      alt={t('dancer:identityVerification.documentFrontAlt')}
                      className="h-32 w-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity hover:opacity-100">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => updateField('frontImageUrl', '')}
                      >
                        <Trans i18nKey="dancer:identityVerification.replace" />
                      </Button>
                    </div>
                    <div className="absolute top-2 right-2">
                      <CheckCircle className="text-success h-5 w-5 drop-shadow-md" />
                    </div>
                  </div>
                ) : (
                  <FileUploader
                    className="min-h-[120px]"
                    maxFiles={1}
                    bucketName="identity-documents"
                    path={`${user?.id}/dancer-identity/${currentValue.countryCode}/${currentValue.documentType}`}
                    allowedMimeTypes={documentRequirement.acceptedFormats}
                    maxFileSize={documentRequirement.maxFileSize}
                    client={supabase}
                    onUploadSuccess={handleFileUploadSuccess('frontImageUrl')}
                    capture="environment"
                    noClick={false}
                    upsert
                  />
                )}
                {errors?.frontImageUrl && (
                  <p className="text-destructive text-xs">
                    {errors.frontImageUrl}
                  </p>
                )}
              </div>

              {/* Back Image (if required) */}
              {documentRequirement.requiresBothSides && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-xs">
                    <FileText className="h-3 w-3" />
                    <Trans i18nKey="dancer:identityVerification.documentBack" />{' '}
                    {required && <span className="text-destructive">*</span>}
                  </Label>
                  {currentValue.backImageUrl ? (
                    <div className="relative overflow-hidden rounded-md border">
                      <StorageImage
                        storagePath={currentValue.backImageUrl}
                        alt={t('dancer:identityVerification.documentBackAlt')}
                        className="h-32 w-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity hover:opacity-100">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => updateField('backImageUrl', '')}
                        >
                          <Trans i18nKey="dancer:identityVerification.replace" />
                        </Button>
                      </div>
                      <div className="absolute top-2 right-2">
                        <CheckCircle className="text-success h-5 w-5 drop-shadow-md" />
                      </div>
                    </div>
                  ) : (
                    <FileUploader
                      className="min-h-[120px]"
                      maxFiles={1}
                      bucketName="identity-documents"
                      path={`${user?.id}/dancer-identity/${currentValue.countryCode}/${currentValue.documentType}`}
                      allowedMimeTypes={documentRequirement.acceptedFormats}
                      maxFileSize={documentRequirement.maxFileSize}
                      client={supabase}
                      onUploadSuccess={handleFileUploadSuccess('backImageUrl')}
                      capture="environment"
                      noClick={false}
                      upsert
                    />
                  )}
                  {errors?.backImageUrl && (
                    <p className="text-destructive text-xs">
                      {errors.backImageUrl}
                    </p>
                  )}
                </div>
              )}

              {/* Selfie (if required) */}
              {documentRequirement.requiresSelfie && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-xs">
                    <Camera className="h-3 w-3" />
                    <Trans i18nKey="dancer:identityVerification.verificationSelfie" />{' '}
                    {required && <span className="text-destructive">*</span>}
                  </Label>

                  {/* Selfie guidance */}
                  <div className="border-info/30 bg-info/10 rounded-md border p-3">
                    <p className="text-info text-xs font-medium">
                      <Trans i18nKey="dancer:identityVerification.howToSelfie" />
                    </p>
                    <ul className="text-info mt-1 list-inside list-disc text-xs">
                      <li>
                        <Trans
                          i18nKey="dancer:identityVerification.selfieTipHold"
                          values={{
                            document:
                              documentRequirement.displayName.toLowerCase(),
                          }}
                        />
                      </li>
                      <li>
                        <Trans i18nKey="dancer:identityVerification.selfieTipVisible" />
                      </li>
                      <li>
                        <Trans i18nKey="dancer:identityVerification.selfieTipLighting" />
                      </li>
                      <li>
                        <Trans i18nKey="dancer:identityVerification.selfieTipCamera" />
                      </li>
                    </ul>
                  </div>

                  {currentValue.selfieImageUrl ? (
                    <div className="relative overflow-hidden rounded-md border">
                      <StorageImage
                        storagePath={currentValue.selfieImageUrl}
                        alt={t('dancer:identityVerification.selfieAlt')}
                        className="h-32 w-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity hover:opacity-100">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => updateField('selfieImageUrl', '')}
                        >
                          <Trans i18nKey="dancer:identityVerification.replace" />
                        </Button>
                      </div>
                      <div className="absolute top-2 right-2">
                        <CheckCircle className="text-success h-5 w-5 drop-shadow-md" />
                      </div>
                    </div>
                  ) : (
                    <FileUploader
                      className="min-h-[120px]"
                      maxFiles={1}
                      bucketName="identity-documents"
                      path={`${user?.id}/dancer-identity/${currentValue.countryCode}/selfies`}
                      allowedMimeTypes={documentRequirement.acceptedFormats}
                      maxFileSize={documentRequirement.maxFileSize}
                      client={supabase}
                      onUploadSuccess={handleFileUploadSuccess(
                        'selfieImageUrl',
                      )}
                      capture="user"
                      noClick={false}
                      upsert
                    />
                  )}
                  {errors?.selfieImageUrl && (
                    <p className="text-destructive text-xs">
                      {errors.selfieImageUrl}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loading state for document upload when user is not yet loaded */}
        {documentRequirement && !documentOnly && !user?.id && (
          <div className="flex items-center justify-center rounded-lg border border-dashed p-6">
            <div className="text-muted-foreground flex items-center gap-2">
              <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
              <span className="text-sm">
                <Trans i18nKey="dancer:identityVerification.loadingDocumentUpload" />
              </span>
            </div>
          </div>
        )}

        {/* Document Dates */}
        {documentRequirement?.expiryRequired && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="flex items-center gap-1 text-xs">
                <Calendar className="h-3 w-3" />
                <Trans i18nKey="dancer:identityVerification.issueDate" />
              </Label>
              <DateField
                max={todayIsoDate}
                value={
                  currentValue.issueDate
                    ? typeof currentValue.issueDate === 'string'
                      ? (currentValue.issueDate.split('T')[0] ?? '')
                      : format(new Date(currentValue.issueDate), 'yyyy-MM-dd')
                    : ''
                }
                onChange={(value) =>
                  updateField('issueDate', value || undefined)
                }
                hasError={Boolean(errors?.issueDate)}
                clearable
              />
              {errors?.issueDate && (
                <p className="text-destructive text-xs">{errors.issueDate}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label className="flex items-center gap-1 text-xs">
                <Calendar className="h-3 w-3" />
                <Trans i18nKey="dancer:identityVerification.expiryDate" />{' '}
                {required && <span className="text-destructive">*</span>}
              </Label>
              <DateField
                min={minVerificationExpiryDate}
                value={
                  currentValue.expiryDate
                    ? typeof currentValue.expiryDate === 'string'
                      ? (currentValue.expiryDate.split('T')[0] ?? '')
                      : format(new Date(currentValue.expiryDate), 'yyyy-MM-dd')
                    : ''
                }
                onChange={(value) =>
                  updateField('expiryDate', value || undefined)
                }
                hasError={Boolean(errors?.expiryDate)}
                clearable
              />
              <p className="text-muted-foreground text-xs">
                <Trans i18nKey="dancer:identityVerification.documentNotExpired" />
              </p>
              {errors?.expiryDate && (
                <p className="text-destructive text-xs">{errors.expiryDate}</p>
              )}
            </div>
          </div>
        )}

        {/* Verification Status */}
        {!documentOnly && currentValue.verificationStatus !== 'pending' && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center gap-2">
                <span>
                  <Trans i18nKey="dancer:identityVerification.verificationStatus" />
                </span>
                <Badge
                  variant={
                    currentValue.verificationStatus === 'verified'
                      ? 'default'
                      : currentValue.verificationStatus === 'rejected'
                        ? 'destructive'
                        : 'secondary'
                  }
                >
                  {currentValue.verificationStatus}
                </Badge>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Privacy Notice */}
        <div className="bg-muted/50 rounded-md p-3">
          <p className="text-muted-foreground text-xs">
            <Shield className="mr-1 inline h-3 w-3" />
            <Trans
              i18nKey={
                documentOnly
                  ? 'dancer:identityVerification.privacyDocumentOnly'
                  : 'dancer:identityVerification.privacyDefault'
              }
            />
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// Export component and types
export type { IdentityVerificationFieldError as IdentityVerificationErrors };

// Type for document-only mode (without file upload fields)
export type DocumentOnlyVerification = Omit<
  IdentityVerification,
  'frontImageUrl' | 'backImageUrl' | 'selfieImageUrl' | 'verificationStatus'
>;
